import { createSelector, createSlice } from '@reduxjs/toolkit';
import { shallowEqual } from 'react-redux';
import { DateTime } from 'luxon';
import { DEFAULT_PAGE_SIZE } from '../constants';

import { AppDispatch, RootState } from '../store';
import { reservationAdapter } from '../entities/reservation.entity';

import {
  ReservationEventDetail,
  ReservationEventType,
} from '../events/reservation';
import {
  TransportReservation,
  TransportReservationRequest,
  TransportReservationResponse,
  TransportTripLeg,
} from '../models/transport-reservation.model';
import { TripLeg } from '../models/reservation.model';
import { TripLegSearchResponseItem } from '../models/trip-leg-search.model';

import { mergeTripLegIntoTransportTripLeg } from '@/services/transportReservationMapper';
import reservationService from '@/services/reservationService';

import { selectAllTripLegs, tripLegSlice } from './trip-leg.slice';
import { driverSlice } from './driver.slice';
import { vehicleSlice } from './vehicle.slice';
import { PagedList } from '../models/paged-list.model';

const DEFAULT_PAGED_LIST: PagedList<string[]> = {
  currentPage: 1,
  totalPages: 0,
  itemCount: 0,
  filters: {
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    sort: 'reservationServiceDate',
    sortOrder: 'desc',
  },
  items: [],
};

// The reservation entity adapter contains all reservations, future and past,
// but we need to fetch future and past reservations separately, so we'll maintain
// separate loading and loaded flags for each. Selectors will be used to get future
// and past reservations from the entity adapter, based on reservationState (active, inactive)
type ReservationEntityState = {
  loadingAllReservations: boolean;
  upcomingReservations: PagedList<string[]>;
  pastReservations: PagedList<string[]>;
  loadedAllReservations: boolean;
  loadingFutureReservations: boolean;
  loadedFutureReservations: boolean;
  loadingPastReservations: boolean;
  loadedPastReservations: boolean;
  reservationEventEmitted: {
    detailType: ReservationEventType;
    detail: ReservationEventDetail;
  } | null;
};

const reservationSlice = createSlice({
  name: 'reservationV2',
  initialState: reservationAdapter.getInitialState<ReservationEntityState>({
    upcomingReservations: { ...DEFAULT_PAGED_LIST },
    pastReservations: { ...DEFAULT_PAGED_LIST },
    loadingAllReservations: false,
    loadedAllReservations: false,
    loadingFutureReservations: false,
    loadedFutureReservations: false,
    loadingPastReservations: false,
    loadedPastReservations: false,
    reservationEventEmitted: null,
  }),
  reducers: {
    setReservations: (
      state,
      action: { payload: TransportReservationResponse },
    ) => {
      reservationAdapter.setMany(state, action.payload.items);
    },
    setUpcomingReservations: (
      state,
      action: { payload: PagedList<TransportReservation> },
    ) => {
      const newState = {
        ...state.upcomingReservations,
        ...action.payload,
        items: action.payload.items.map(
          (item) => item.reservationReferenceID as unknown as string[],
        ),
      };
      state.upcomingReservations = newState;
    },
    setPastReservations: (
      state,
      action: { payload: PagedList<TransportReservation> },
    ) => {
      const newState = {
        ...state.pastReservations,
        ...action.payload,
        items: action.payload.items.map(
          (item) => item.reservationReferenceID as unknown as string[],
        ),
      };
      state.pastReservations = newState;
    },
    setLoadingAllReservations: (state, action: { payload: boolean }) => {
      state.loadingAllReservations = action.payload;
    },
    setLoadedAllReservations: (state, action: { payload: boolean }) => {
      state.loadedAllReservations = action.payload;
    },
    setLoadingFutureReservations: (state, action: { payload: boolean }) => {
      state.loadingFutureReservations = action.payload;
    },
    setLoadedFutureReservations: (state, action: { payload: boolean }) => {
      state.loadedFutureReservations = action.payload;
    },
    setLoadingPastReservations: (state, action: { payload: boolean }) => {
      state.loadingPastReservations = action.payload;
    },
    setLoadedPastReservations: (state, action: { payload: boolean }) => {
      state.loadedPastReservations = action.payload;
    },
    setReservationEventEmitted: (
      state,
      action: {
        payload: {
          detailType: ReservationEventType;
          detail: ReservationEventDetail;
        } | null;
      },
    ) => {
      state.reservationEventEmitted = action.payload;
    },
    reservationActivated: (
      state,
      action: { payload: TransportReservation },
    ) => {
      reservationAdapter.addOne(state, action.payload);
    },
  },
});

const {
  setReservations,
  setUpcomingReservations,
  setPastReservations,
  setLoadingAllReservations,
  setLoadedAllReservations,
  setLoadingFutureReservations,
  setLoadedFutureReservations,
  setLoadingPastReservations,
  setLoadedPastReservations,
  setReservationEventEmitted,
} = reservationSlice.actions;

/*
 * Utility function to upsert entity data into the other entity adapters
 * since reservation payloads tend to or can contain a lof of associated data
 * Should be included in every async reservation fetch to ensure we always update
 * entities with the latest API data.
 */
const upsertEntityData = (
  dispatch: AppDispatch,
  response: TransportReservationResponse,
) => {
  // Check for any trip legs that were returned, and upsert
  // them into the trip leg entity adapter
  dispatch(
    tripLegSlice.actions.upsertTripLegsFromTransportReservation(
      response.items.flatMap((reservation) => reservation.tripLegs),
    ),
  );
  // Check for any driver data that was returned, and upsert
  // them into the driver entity adapter
  dispatch(
    driverSlice.actions.upsertDrivers(
      response.items.flatMap((reservation) =>
        reservation.tripLegs
          .map((tripLeg) => tripLeg.driver)
          .filter((driver) => driver !== null),
      ),
    ),
  );
  // Check for any vehicle data that was returned, and upsert
  // them into the vehicle entity adapter
  dispatch(
    vehicleSlice.actions.upsertVehicles(
      response.items.flatMap((reservation) =>
        reservation.tripLegs
          .map((tripLeg) => tripLeg.vehicle)
          .filter((vehicle) => vehicle !== null),
      ),
    ),
  );
};

/*
 * Fetch functions for use in components to fetch reservations from the API
 */
const fetchAllReservationsAsync = (request: TransportReservationRequest) => {
  return async (dispatch: AppDispatch) => {
    dispatch(setLoadingAllReservations(true));
    dispatch(setLoadedAllReservations(false));
    reservationService.getPassengerTransportReservations(request).subscribe({
      next: (response) => {
        dispatch(setReservations(response));
        upsertEntityData(dispatch, response);
        dispatch(setLoadingAllReservations(false));
        dispatch(setLoadedAllReservations(true));
      },
    });
  };
};

const fetchFutureReservationsAsync = (
  request: TransportReservationRequest,
  abortSignal?: AbortSignal,
) => {
  return async (dispatch: AppDispatch) => {
    dispatch(setLoadingFutureReservations(true));
    dispatch(setLoadedFutureReservations(false));
    reservationService
      .getPassengerTransportReservations(request, abortSignal)
      .subscribe({
        next: (response) => {
          dispatch(setReservations(response));
          upsertEntityData(dispatch, response);
          dispatch(
            setUpcomingReservations(
              response as unknown as PagedList<TransportReservation>,
            ),
          );
          dispatch(setLoadingFutureReservations(false));
          dispatch(setLoadedFutureReservations(true));
        },
      });
  };
};

const fetchPastReservationsAsync = (request: TransportReservationRequest) => {
  return async (dispatch: AppDispatch) => {
    dispatch(setLoadingPastReservations(true));
    dispatch(setLoadedPastReservations(false));
    reservationService.getPassengerTransportReservations(request).subscribe({
      next: (response) => {
        dispatch(setReservations(response));
        upsertEntityData(dispatch, response);
        dispatch(
          setPastReservations(
            response as unknown as PagedList<TransportReservation>,
          ),
        );
        dispatch(setLoadingPastReservations(false));
        dispatch(setLoadedPastReservations(true));
      },
    });
  };
};

/*
 * Selectors for use in components to get reservations from the state
 */
const reservationSelectors = reservationAdapter.getSelectors<RootState>(
  (state) => state.reservationV2,
);

// Utility function to check the tripLegV2 entities for any trip legs
// that were updated via realtime updates so we can merge them into the reservations
const mergeReservationsWithTripLegs = (
  reservations: TransportReservation[],
  tripLegEntities: TripLegSearchResponseItem[],
) => {
  const tripLegsByReferenceId = new Map(
    tripLegEntities.map((entity) => [entity.tripLegReferenceID, entity]),
  );

  return reservations.map((reservation) => ({
    ...reservation,
    tripLegs: reservation.tripLegs.map((reservationTripLeg) => {
      const entity = tripLegsByReferenceId.get(
        reservationTripLeg.tripLegReferenceID,
      );
      if (!entity) {
        return reservationTripLeg;
      }

      return mergeTripLegIntoTransportTripLeg(
        entity as unknown as TransportTripLeg,
        entity as unknown as TripLeg,
      );
    }),
  }));
};

// Iterate through the Reservations' Trip Legs, filtering for specific parameters
const filterTripLegs = (
  reservations: TransportReservation[],
  startDateTimeUTC: string | null,
  endDateTimeUTC: string | null,
) => {
  return reservations.map((reservation) => ({
    ...reservation,
    tripLegs: reservation.tripLegs.filter((tripLeg) => {
      let appointmentDateTimeUTC = tripLeg.appointmentTimeUTC;
      let startTimeFilter = startDateTimeUTC;
      let endTimeFilter = endDateTimeUTC;

      // If Will Call, set the appointment date time to the end of the day
      if (tripLeg.isWillCall) {
        appointmentDateTimeUTC = DateTime.fromISO(tripLeg.appointmentDate)
          .endOf('day')
          .toUTC()
          .toISO();
        startTimeFilter = DateTime.fromISO(startDateTimeUTC ?? '')
          .endOf('day')
          .toUTC()
          .toISO();
        endTimeFilter = DateTime.fromISO(endDateTimeUTC ?? '')
          .endOf('day')
          .toUTC()
          .toISO();
      }

      if (startTimeFilter && endTimeFilter && appointmentDateTimeUTC) {
        return (
          appointmentDateTimeUTC >= startTimeFilter &&
          appointmentDateTimeUTC <= endTimeFilter
        );
      } else if (startTimeFilter && appointmentDateTimeUTC) {
        return appointmentDateTimeUTC >= startTimeFilter;
      } else if (endTimeFilter && appointmentDateTimeUTC) {
        return appointmentDateTimeUTC < endTimeFilter;
      }
    }),
  }));
};

const mergedTripLegsSelectorOptions = {
  memoizeOptions: { resultEqualityCheck: shallowEqual },
};

// Select all reservations
const selectAllReservations = createSelector(
  [reservationSelectors.selectAll, selectAllTripLegs],
  mergeReservationsWithTripLegs,
  mergedTripLegsSelectorOptions,
);

const selectReservationsMergedWithTripLegs = createSelector(
  [reservationSelectors.selectAll, selectAllTripLegs],
  mergeReservationsWithTripLegs,
  mergedTripLegsSelectorOptions,
);

const selectReservationsByDateRange = createSelector(
  [
    selectReservationsMergedWithTripLegs,
    (_state: RootState, startDate: string | null) => startDate,
    (_state: RootState, _start: string | null, endDate: string | null) =>
      endDate,
  ],
  (reservations, startDate, endDate) =>
    filterTripLegs(reservations, startDate, endDate),
  mergedTripLegsSelectorOptions,
);

// Selectors for each view's state
const selectUpcomingReservationsState = (state: RootState) =>
  state.reservationV2.upcomingReservations;
const selectPastReservationsState = (state: RootState) =>
  state.reservationV2.pastReservations;

// Selectors for listing specific IDs
const selectUpcomingReservationsIDs = createSelector(
  [selectAllReservations, selectUpcomingReservationsState],
  (entities, tripManagementTodaysTripsState) => {
    const ids = entities.map(
      (entity: TransportReservation) => entity.reservationReferenceID,
    );
    return tripManagementTodaysTripsState.items.filter((id) =>
      ids.includes(id as unknown as string),
    );
  },
  { memoizeOptions: { resultEqualityCheck: shallowEqual } },
);

const selectPastReservationsIDs = createSelector(
  [selectAllReservations, selectPastReservationsState],
  (entities, pastReservationsState) => {
    const ids = entities.map(
      (entity: TransportReservation) => entity.reservationReferenceID,
    );
    return pastReservationsState.items.filter((id) =>
      ids.includes(id as unknown as string),
    );
  },
  { memoizeOptions: { resultEqualityCheck: shallowEqual } },
);

/*
 * Derived selectors for entities mapped to IDs retrieved from API requests,
 * These are what should be used in the view
 */

// For use in Member Portal's Home page and Trips page, showing a list of upcoming reservations
const selectUpcomingReservations = createSelector(
  [
    selectAllReservations,
    selectUpcomingReservationsIDs,
    selectUpcomingReservationsState,
  ],
  (entities, upcomingReservationsIDs, upcomingReservationsState) => {
    return {
      currentPage: upcomingReservationsState.currentPage,
      totalPages: upcomingReservationsState.totalPages,
      itemCount: upcomingReservationsState.itemCount,
      filters: upcomingReservationsState.filters,
      items: entities
        .filter((entity: TransportReservation) =>
          upcomingReservationsIDs.includes(
            entity.reservationReferenceID as unknown as string[],
          ),
        )
        .sort(
          (a, b) =>
            upcomingReservationsState.items.indexOf(
              a.reservationReferenceID as unknown as string[],
            ) -
            upcomingReservationsState.items.indexOf(
              b.reservationReferenceID as unknown as string[],
            ),
        ),
    };
  },
  { memoizeOptions: { resultEqualityCheck: shallowEqual } },
);

// For use in Member Portal's Trips page, showing a list of past reservations
const selectPastReservations = createSelector(
  [
    selectAllReservations,
    selectPastReservationsIDs,
    selectPastReservationsState,
  ],
  (entities, pastReservationsIDs, pastReservationsState) => {
    return {
      currentPage: pastReservationsState.currentPage,
      totalPages: pastReservationsState.totalPages,
      itemCount: pastReservationsState.itemCount,
      filters: pastReservationsState.filters,
      items: entities
        .filter((entity: TransportReservation) =>
          pastReservationsIDs.includes(
            entity.reservationReferenceID as unknown as string[],
          ),
        )
        .sort(
          (a, b) =>
            pastReservationsState.items.indexOf(
              a.reservationReferenceID as unknown as string[],
            ) -
            pastReservationsState.items.indexOf(
              b.reservationReferenceID as unknown as string[],
            ),
        ),
    };
  },
  { memoizeOptions: { resultEqualityCheck: shallowEqual } },
);

const selectLoadingAllReservations = (state: RootState) =>
  state.reservationV2.loadingAllReservations;
const selectLoadedAllReservations = (state: RootState) =>
  state.reservationV2.loadedAllReservations;
const selectLoadingFutureReservations = (state: RootState) =>
  state.reservationV2.loadingFutureReservations;
const selectLoadedFutureReservations = (state: RootState) =>
  state.reservationV2.loadedFutureReservations;
const selectLoadingPastReservations = (state: RootState) =>
  state.reservationV2.loadingPastReservations;
const selectLoadedPastReservations = (state: RootState) =>
  state.reservationV2.loadedPastReservations;

const selectReservationEventEmitted = (state: RootState) =>
  state.reservationV2.reservationEventEmitted;

export {
  reservationSlice,
  reservationSelectors,
  fetchAllReservationsAsync,
  fetchFutureReservationsAsync,
  fetchPastReservationsAsync,
  selectAllReservations,
  selectLoadingAllReservations,
  selectLoadedAllReservations,
  selectReservationsByDateRange,
  selectUpcomingReservations,
  selectPastReservations,
  selectLoadingFutureReservations,
  selectLoadedFutureReservations,
  selectLoadingPastReservations,
  selectLoadedPastReservations,
  selectReservationEventEmitted,
};
